import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Anchor, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1672939113767-eb78926d5a08?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, ready, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && user) navigate("/", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      navigate("/", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-navy px-4">
      <img src={HERO} alt="yacht" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-navy/85" />
      <div className="animate-slide-up relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/logo.png" alt="logo" className="mb-3 h-20 w-20 rounded-2xl shadow-lg" />
          <h1 className="font-head text-3xl font-bold tracking-tight text-navy">YACHT FOOD COST</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
            <Anchor className="h-4 w-4 text-gold" /> Gestionale Food Cost & Cambusa
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-navy">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-14 w-full rounded-lg border-2 border-slate-200 px-4 text-lg outline-none focus:border-gold"
              placeholder="nome@yacht.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-navy">Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-14 w-full rounded-lg border-2 border-slate-200 px-4 text-lg outline-none focus:border-gold"
              placeholder="••••••••"
            />
          </div>
          {err && <div data-testid="login-error" className="rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600">{err}</div>}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-navy text-lg font-semibold text-white transition-colors hover:bg-navy-hover disabled:opacity-60"
          >
            {loading && <Loader2 className="h-5 w-5 animate-spin" />}
            Accedi
          </button>
        </form>

        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">oppure</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <button
          data-testid="google-login-btn"
          onClick={googleLogin}
          className="flex h-14 w-full items-center justify-center gap-3 rounded-lg border-2 border-slate-200 bg-white text-base font-semibold text-navy transition-colors hover:border-gold hover:bg-slate-50"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continua con Google
        </button>

        <div className="mt-6 border-t border-slate-100 pt-4">
          <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">Accesso rapido demo</p>
          <div className="grid grid-cols-3 gap-2">
            <button data-testid="quick-chef" onClick={() => quick("tommasomarin657@gmail.com", "Titangela2026")} className="rounded-lg bg-slate-100 py-2 text-xs font-medium hover:bg-slate-200">Chef</button>
            <button data-testid="quick-comandante" onClick={() => quick("comandante@titangela.com", "Comandante2026")} className="rounded-lg bg-slate-100 py-2 text-xs font-medium hover:bg-slate-200">Comandante</button>
            <button data-testid="quick-armatore" onClick={() => quick("armatore@titangela.com", "Armatore2026")} className="rounded-lg bg-slate-100 py-2 text-xs font-medium hover:bg-slate-200">Armatore</button>
          </div>
        </div>
      </div>
    </div>
  );
}
